import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Rating,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Container,
  Grid,
  Card,
  CardContent,
  Divider,
  Stack
} from '@mui/material';
import {
  Star,
  CheckCircle,
  Error as ErrorIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

interface ReservationData {
  grc_no: string;
  guest_name: string;
  property_name: string;
  property_id: string;
  arrival_date: string;
  departure_date: string;
  already_submitted: boolean;
}

interface FeedbackData {
  overall_rating: number;
  cleanliness_rating: number;
  service_rating: number;
  food_rating: number;
  value_rating: number;
  comments: string;
  would_recommend: boolean;
}

const PublicFeedbackForm: React.FC = () => {
  const { propertyId, grcNo } = useParams<{ propertyId: string; grcNo: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reservation, setReservation] = useState<ReservationData | null>(null);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  
  const [feedback, setFeedback] = useState<FeedbackData>({
    overall_rating: 0,
    cleanliness_rating: 0,
    service_rating: 0,
    food_rating: 0,
    value_rating: 0,
    comments: '',
    would_recommend: false
  });

  useEffect(() => {
    if (grcNo && propertyId) {
      fetchReservation();
    }
  }, [grcNo, propertyId]);

  const fetchReservation = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(
        `${API_BASE_URL}/public/feedback/${encodeURIComponent(propertyId!)}/${encodeURIComponent(grcNo!)}/`
      );
      
      if (response.data.status === 'success') {
        setReservation(response.data.data);
        
        if (response.data.data.already_submitted) {
          setError('Feedback has already been submitted for this reservation.');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load reservation details. Please check your GRC number.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all ratings are filled
    if (
      feedback.overall_rating === 0 ||
      feedback.cleanliness_rating === 0 ||
      feedback.service_rating === 0 ||
      feedback.food_rating === 0 ||
      feedback.value_rating === 0
    ) {
      setError('Please provide all ratings before submitting.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      
      const response = await axios.post(`${API_BASE_URL}/public/feedback/submit/`, {
        grc_no: grcNo,
        property_id: propertyId,
        ...feedback
      });

      if (response.data.status === 'success') {
        setSuccess(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
        p={3}
      >
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Thank You!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your feedback has been submitted successfully. We appreciate you taking the time to share your experience with us.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Guest: <strong>{reservation?.guest_name}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Property: <strong>{reservation?.property_name}</strong>
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (error && !reservation) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
        p={3}
      >
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Reservation Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {error}
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box minHeight="100vh" bgcolor="#f5f5f5" py={4}>
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ overflow: 'hidden' }}>
          {/* Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              p: 4,
              textAlign: 'center'
            }}
          >
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              We Value Your Feedback
            </Typography>
            <Typography variant="body1">
              Help us improve your experience at {reservation?.property_name}
            </Typography>
          </Box>

          {/* Guest Information */}
          <Box sx={{ p: 3, bgcolor: '#f9fafb' }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Guest Name
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {reservation?.guest_name}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Property
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {reservation?.property_name}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Feedback Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ p: 4 }}>
            {error && reservation?.already_submitted && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {error && !reservation?.already_submitted && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Stack spacing={4}>
              {/* Overall Rating */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Overall Experience
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    How would you rate your overall experience?
                  </Typography>
                  <Rating
                    name="overall_rating"
                    value={feedback.overall_rating}
                    onChange={(_, value) => setFeedback({ ...feedback, overall_rating: value || 0 })}
                    size="large"
                    icon={<Star fontSize="inherit" />}
                    emptyIcon={<Star fontSize="inherit" />}
                    disabled={reservation?.already_submitted}
                  />
                </CardContent>
              </Card>

              {/* Cleanliness Rating */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Cleanliness
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    How would you rate the cleanliness?
                  </Typography>
                  <Rating
                    name="cleanliness_rating"
                    value={feedback.cleanliness_rating}
                    onChange={(_, value) => setFeedback({ ...feedback, cleanliness_rating: value || 0 })}
                    size="large"
                    disabled={reservation?.already_submitted}
                  />
                </CardContent>
              </Card>

              {/* Service Rating */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Service Quality
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    How would you rate our service?
                  </Typography>
                  <Rating
                    name="service_rating"
                    value={feedback.service_rating}
                    onChange={(_, value) => setFeedback({ ...feedback, service_rating: value || 0 })}
                    size="large"
                    disabled={reservation?.already_submitted}
                  />
                </CardContent>
              </Card>

              {/* Food Rating */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Food & Dining
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    How would you rate the food quality?
                  </Typography>
                  <Rating
                    name="food_rating"
                    value={feedback.food_rating}
                    onChange={(_, value) => setFeedback({ ...feedback, food_rating: value || 0 })}
                    size="large"
                    disabled={reservation?.already_submitted}
                  />
                </CardContent>
              </Card>

              {/* Value Rating */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Value for Money
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    How would you rate the value for money?
                  </Typography>
                  <Rating
                    name="value_rating"
                    value={feedback.value_rating}
                    onChange={(_, value) => setFeedback({ ...feedback, value_rating: value || 0 })}
                    size="large"
                    disabled={reservation?.already_submitted}
                  />
                </CardContent>
              </Card>

              {/* Comments */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Additional Comments
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Please share any additional feedback or suggestions..."
                    value={feedback.comments}
                    onChange={(e) => setFeedback({ ...feedback, comments: e.target.value })}
                    disabled={reservation?.already_submitted}
                  />
                </CardContent>
              </Card>

              {/* Would Recommend */}
              <Card variant="outlined">
                <CardContent>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={feedback.would_recommend}
                        onChange={(e) => setFeedback({ ...feedback, would_recommend: e.target.checked })}
                        disabled={reservation?.already_submitted}
                      />
                    }
                    label="I would recommend this property to others"
                  />
                </CardContent>
              </Card>

              {/* Submit Button */}
              {!reservation?.already_submitted && (
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }}
                >
                  {submitting ? <CircularProgress size={24} color="inherit" /> : 'Submit Feedback'}
                </Button>
              )}
            </Stack>
          </Box>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 3, color: 'text.secondary' }}>
          <Typography variant="body2">
            © 2025 {reservation?.property_name}. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PublicFeedbackForm;

